import { useState } from 'react';
import { apiUrl, formatBytes, mediaDownloadUrl, type Message } from '@wa/shared';
import { MediaViewer } from '@/components/MediaViewer';

/**
 * The attachment inside a bubble.
 *
 * Inbound media arrives as an id and the bytes follow a moment later, so there
 * are three states to render, not one: waiting, ready, and never arriving. A
 * spinner that never resolves is worse than saying the attachment is lost.
 */
export function MediaBubble({ message, senderName, onForward }: {
  message: Message;
  /** For the viewer's header. */
  senderName?: string;
  /** Opens the forward picker for this message; absent where forwarding is not offered. */
  onForward?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // The API hands back a path; the portal is on another origin.
  const src = message.mediaUrl ? apiUrl(message.mediaUrl) : null;

  if (message.mediaStatus === 2) {
    return (
      <div className="flex items-center gap-2 rounded bg-black/5 px-2.5 py-2 text-small text-(--color-muted) dark:bg-white/5">
        <Icon kind="warning" />
        <span>Attachment unavailable — it expired before we could fetch it.</span>
      </div>
    );
  }

  // Refused on purpose, and said so: a .iso called "Bank Statement" with a
  // paragraph urging the finance manager to open it is the shape of most of
  // these. The agent sees the name, the size, and that nobody here can open it.
  if (message.mediaStatus === 3) {
    return (
      <div className="flex items-start gap-2 rounded bg-(--color-bad-wash) px-2.5 py-2 text-small">
        <Icon kind="warning" />
        <span className="min-w-0">
          <span className="block truncate font-medium text-(--color-ink)">
            {message.mediaFileName ?? 'Attachment'}
            {message.mediaByteSize ? ` · ${formatBytes(message.mediaByteSize)}` : ''}
          </span>
          <span className="block text-(--color-muted)">
            Blocked — {message.mediaBlockedReason ?? 'this file type can carry malware and is never downloaded.'}
          </span>
        </span>
      </div>
    );
  }

  if (!src) {
    return (
      <div className="flex items-center gap-2 rounded bg-black/5 px-2.5 py-2 text-small text-(--color-muted) dark:bg-white/5">
        <span className="h-3.5 w-3.5 shrink-0 animate-pulse rounded-full bg-(--color-muted)" />
        <span>Downloading attachment…</span>
      </div>
    );
  }

  const kind = message.mediaMimeType?.split('/')[0];

  if (kind === 'image') {
    return (
      <>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="block overflow-hidden rounded"
          aria-label="Open image full size"
        >
          {/* Bounded so a 4000px photo cannot decide the bubble's width, but
              the whole picture — object-cover cropped a tall screenshot to its
              middle third, and the top of a screenshot is usually the point. */}
          <img
            src={src}
            alt={message.mediaFileName ?? 'Received image'}
            loading="lazy"
            className="block h-auto max-h-80 w-auto max-w-sm min-w-40 rounded object-contain"
          />
        </button>

        {expanded && (
          <MediaViewer
            message={message}
            src={src}
            senderName={senderName ?? (message.direction === 1 ? (message.sentByAgentName ?? 'You') : 'Customer')}
            onClose={() => setExpanded(false)}
            onForward={onForward ? () => { setExpanded(false); onForward(); } : undefined}
          />
        )}
      </>
    );
  }

  if (kind === 'video') {
    return (
      <div className="max-w-sm">
        <video src={src} controls preload="metadata" className="max-h-72 w-full rounded" />
        <DownloadLink message={message} />
      </div>
    );
  }

  if (kind === 'audio') {
    return (
      <div className="max-w-xs">
        <audio src={src} controls preload="metadata" className="w-full" />
        <DownloadLink message={message} />
      </div>
    );
  }

  return (
    <a
      href={mediaDownloadUrl(message.mediaUrl!)}
      className="tap flex items-center gap-2.5 rounded bg-black/5 px-2.5 py-2 no-underline dark:bg-white/5"
    >
      <Icon kind="document" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-small font-medium text-(--color-ink)">
          {message.mediaFileName ?? 'Document'}
        </span>
        <span className="block text-micro text-(--color-muted)">
          {formatBytes(message.mediaByteSize)}
        </span>
      </span>
    </a>
  );
}

/**
 * A save-to-disk link under a player. The player's own menu has one on some
 * browsers and not others, and on none of them does it work across origins
 * without the server saying attachment — which ?download=1 makes it say.
 */
function DownloadLink({ message }: { message: Message }) {
  if (!message.mediaUrl) return null;
  return (
    <a
      href={mediaDownloadUrl(message.mediaUrl)}
      className="tap mt-1 inline-flex items-center gap-1 text-micro text-(--color-muted) no-underline hover:text-(--color-ink)"
    >
      <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
        <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M4 15.5h12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Download{message.mediaByteSize ? ` · ${formatBytes(message.mediaByteSize)}` : ''}
    </a>
  );
}

function Icon({ kind }: { kind: 'document' | 'warning' }) {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" className="shrink-0 text-(--color-muted)">
      {kind === 'document' ? (
        <>
          <path d="M5 2.5h6L15.5 7v10.5h-11z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M11 2.5V7h4.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M10 6v4.6M10 13.4v.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}
